import React, {useState, useRef, useEffect, useMemo} from 'react';
import EditIcon from '../../assets/icons/Edit.svg';
import PinIcon from '../../assets/icons/Map_pin.svg';
import ClockIcon from '../../assets/icons/Clock.svg';
import Coffee from '../../assets/icons/Clipboard.svg';
import User from '../../assets/icons/User.svg';
import Users from '../../assets/icons/Users.svg';
import Tool from '../../assets/icons/Tool.svg';
import Unlock from '../../assets/icons/Unlock.svg';
import Lock from '../../assets/icons/LockBold.svg';
import Check from '../../assets/icons/Check.svg';
import Plus from '../../assets/icons/Plus.svg';
import CoffeeBG from '../../assets/icons/CoffeeBG.svg';
import UserBG from '../../assets/icons/UserBG.svg';
import UsersBG from '../../assets/icons/UsersBG.svg';
import ToolBG from '../../assets/icons/ToolBG.svg';
import Trash from '../../assets/icons/Trash.svg';

import apiClient from "../../context/apiClient";

const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

const languageFlags = {
    DE: "🇩🇪", EN: "🇬🇧", FR: "🇫🇷", ES: "🇪🇸", IT: "🇮🇹",
    NE: "🇳🇱", PL: "🇵🇱", TR: "🇹🇷", PT: "🇵🇹", RU: "🇷🇺",
    AR: "🇸🇦", ZH: "🇨🇳", JA: "🇯🇵", KO: "🇰🇷", SE: "🇸🇪",
    NO: "🇳🇴", FI: "🇫🇮", DK: "🇩🇰", HU: "🇭🇺", CZ: "🇨🇿",
    SK: "🇸🇰", RO: "🇷🇴", BG: "🇧🇬", HR: "🇭🇷", EL: "🇬🇷",
};

const parseAddressString = (addressString) => {
    if (!addressString || typeof addressString !== 'string')
        return {street: '', streetNumber: '', postalCode: '', city: '', country: ''};

    const fields = ['street', 'streetNumber', 'postalCode', 'city', 'country'];
    const regex = /(\w+)=([^,)]*)/g;
    const result = {};
    let match;

    while ((match = regex.exec(addressString)) !== null) {
        const key = match[1];
        const value = match[2]?.trim();
        if (fields.includes(key)) {
            result[key] = value || '';
        }
    }

    // Fallbacks für fehlende Felder
    fields.forEach(field => {
        if (!result[field]) result[field] = '';
    });

    return result;
};

const formatLocation = (location) => {
    if (!location) return '';
    const {street, streetNumber, postalCode, city, country} = location;
    return [
        street && streetNumber ? `${street} ${streetNumber}` : null,
        postalCode || city ? `${postalCode ?? ''} ${city ?? ''}`.trim() : null,
        country,
    ]
        .filter(Boolean)
        .join(', ');
};

const doesEmployeeFulfillQualifications = (employee, role, requiredQualifications = []) => {
    if (!employee || !Array.isArray(employee.qualifications)) return false;

    const roleSuffixMap = {
        LEADER: 'KL',
        INSTRUCTOR: null,
        MECHANIC: 'M',
        GUEST_MANAGER: 'GM',
    };

    const relevantSuffix = roleSuffixMap[role];

    const requiredForRole = requiredQualifications
        .map(q => typeof q === 'string' ? q : q?.name || '')
        .filter((q) => {
            const match = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
            const suffix = match?.[2] || null;

            if (relevantSuffix === null) {
                // INSTRUCTOR → nur ohne Suffix
                return !suffix;
            }

            return suffix === relevantSuffix;
        });

    const employeeQualNames = employee.qualifications.map((q) =>
        typeof q === 'string' ? q : q?.name || ''
    );

    return requiredForRole.every((reqQ) => employeeQualNames.includes(reqQ));
};


const EventCard = ({event, allQualifications, onUpdated, onLockChange, lockedMap = {}, onEventTimeframeChanged, showFadeTop = false, showFadeBottom = false, onDeleted }) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [showQualificationModal, setShowQualificationModal] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [timeError, setTimeError] = useState(false);
    const [bestEmployeesMap, setBestEmployeesMap] = useState({});
    const [loadingMap, setLoadingMap] = useState({});
    const [hoveredLockKey, setHoveredLockKey] = useState(null);
    const [localEditData, setLocalEditData] = useState({
        name: event.name || '',
        timeframe: event.timeframe || {},
        location: parseAddressString(event.location),
        numberLeader: event.numberLeader || 0,
        numberInstructors: event.numberInstructors || 0,
        numberMechanics: event.numberMechanics || 0,
        numberGuestManagers: event.numberGuestManagers || 0,
        qualifications: Array.isArray(event.qualifications)
            ? event.qualifications.map((q) => (typeof q === 'string' ? q : q?.name || ''))
            : [],
        languages: Array.isArray(event.languages)
            ? event.languages.map((l) => (typeof l === 'string' ? l : l?.code || ''))
            : [],
    });
    const isEditable = (event?.eventStatus ?? '').trim().toUpperCase() === 'OPEN';

    const cardRadiusClass = (() => {
        if (showFadeTop && showFadeBottom) return 'rounded-none';
        if (showFadeTop) return 'rounded-b-xl rounded-t-none';
        if (showFadeBottom) return 'rounded-t-xl rounded-b-none';
        return 'rounded-xl';
    })();


    const fetchBestEmployees = async (eventId, role, rowKey) => {
        setLoadingMap(prev => ({...prev, [rowKey]: true}));

        try {
            const response = await apiClient.get(`/api/events/${eventId}/bestEmployees?role=${role}`);
            const data = response.data;

            setBestEmployeesMap(prev => ({
                ...prev,
                [rowKey]: data,
            }));
        } catch (err) {
            console.error('❌ Fehler beim Laden der besten Mitarbeiter:', err);
        } finally {
            setLoadingMap(prev => ({...prev, [rowKey]: false}));
        }
    };

    const instructorOptions = [];
    const mechanicOptions = [];
    const guestManagerOptions = [];

    const [showLanguageSelect, setShowLanguageSelect] = useState(false);
    const dropdownRef = useRef(null);

    // Sprachen hinzufügen/entfernen
    const handleAddLanguage = (lang) => {
        if (!localEditData.languages.includes(lang)) {
            setLocalEditData((prev) => ({
                ...prev,
                languages: [...prev.languages, lang],
            }));
        }
    };

    const updateEmployeeInEvent = (role, idx, employee) => {
        if (role === 'LEADER' && idx === 0) {
            event.leader = employee;
        } else {
            if (!event.employeeList) event.employeeList = [];
            const roleList = employeesByRole[role] || [];
            const oldEmployee = roleList[idx];
            const existingIndex = event.employeeList.findIndex(e => e.id === oldEmployee?.id);
            if (existingIndex !== -1) {
                event.employeeList[existingIndex] = employee;
            } else {
                event.employeeList.push(employee);
            }
        }
    };

    const handleRemoveLanguage = (lang) => {
        setLocalEditData((prev) => ({
            ...prev,
            languages: prev.languages.filter((l) => l !== lang),
        }));
    };

    const handleUpdateEvent = async (updatedEventData) => {
        const oldEvent = {...event};

        const stringifyAddress = (location) => {
            if (!location) return '';
            const {street, streetNumber, postalCode, city, country} = location;
            return `${street || ''}, ${streetNumber || ''}, ${postalCode || ''}, ${city || ''}, ${country || ''}`;
        };

        const payload = {
            name: localEditData.name,
            timeframe: localEditData.timeframe,
            location: stringifyAddress(localEditData.location),
            leader: event.leader ?? null,
            employeeList: (() => {
                const cleaned = (event.employeeList ?? []).filter(
                    (e) => e && (e.id || e.firstName || e.lastName)
                );
                return cleaned.length > 0 ? cleaned : null;
            })(),
            numberLeader: localEditData.numberLeader || 0,
            numberInstructors: localEditData.numberInstructors || 0,
            numberMechanics: localEditData.numberMechanics || 0,
            numberGuestManagers: localEditData.numberGuestManagers || 0,
            qualifications: localEditData.qualifications.map((q) => ({name: q})),
            languages: localEditData.languages,
        };

        try {
            console.log("🚀 Sende Event-Payload:", payload);
            const response = await apiClient.put(`/api/events/updateEvent/${event.id}`, payload);
            if (response.status === 200) {
                console.log('✅ Event wurde aktualisiert');
                setIsFlipped(false);
                if (onEventTimeframeChanged) {
                    onEventTimeframeChanged(oldEvent, {...event, ...payload});
                }
                onUpdated?.();
            } else {
                const errorText = await response.text();
                console.error('⚠️ Fehler:', errorText);
                alert('Fehler beim Speichern des Events.');
            }
        } catch (err) {
            console.error('❌ Netzwerkfehler:', err);
            alert('Netzwerkfehler – bitte später erneut versuchen.');
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Bist du sicher, dass du dieses Event löschen möchtest?')) {
            return;
        }

        try {
            const response = await apiClient.delete(`/api/events/deleteEvent/${event.id}`);
            if (response.status >= 200 && response.status < 300) {
                console.log('✅ Event erfolgreich gelöscht');
                // Notify parent to remove event immediately from UI
                onDeleted?.(event.id);
                // Also request a full refresh from backend (keeps client in sync)
                onUpdated?.();
            } else {
                const errorText = await response.text();
                console.error('⚠️ Fehler beim Löschen:', errorText);
                alert('Fehler beim Löschen des Events.');
            }
        } catch (err) {
            console.error('❌ Netzwerkfehler:', err);
            alert('Netzwerkfehler – bitte später erneut versuchen.');
        }
    };


    (allQualifications || []).forEach((q) => {
        const match = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
        const base = match?.[1] || q;
        const suffix = match?.[2] || null;

        if ((localEditData.qualifications || []).includes(q)) return;

        if (!suffix || suffix === "KL") {
            instructorOptions.push({
                label: suffix === "KL" ? `${base} (KL)` : base,
                value: q,
            });
        } else if (suffix === "M") {
            mechanicOptions.push({label: base, value: q});
        } else if (suffix === "GM") {
            guestManagerOptions.push({label: base, value: q});
        }
    });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowLanguageSelect(false);
            }
        };

        if (showLanguageSelect) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showLanguageSelect]);

    instructorOptions.sort((a, b) => a.label.localeCompare(b.label, 'de'));
    mechanicOptions.sort((a, b) => a.label.localeCompare(b.label, 'de'));
    guestManagerOptions.sort((a, b) => a.label.localeCompare(b.label, 'de'));

    const employeesByRole = useMemo(() => {
        const map = {
            LEADER: [],
            INSTRUCTOR: [],
            MECHANIC: [],
            GUEST_MANAGER: [],
        };

        (event.employeeList || []).forEach(emp => {
            if (emp?.role && map[emp.role]) {
                map[emp.role].push(emp);
            }
        });

        return map;
    }, [event.employeeList]);

    const handleDropdownFocus = (rowKey, role) => {
        setBestEmployeesMap(prev => ({...prev, [rowKey]: []}));
        fetchBestEmployees(event.id, role, rowKey).catch(err => {
            console.error(`❌ Error fetching best employees for ${rowKey}:`, err);
        });
    };

    useEffect(() => {
        const roles = [
            {key: 'numberLeader', role: 'LEADER'},
            {key: 'numberInstructors', role: 'INSTRUCTOR'},
            {key: 'numberMechanics', role: 'MECHANIC'},
            {key: 'numberGuestManagers', role: 'GUEST_MANAGER'},
        ];

        roles.forEach(({key, role}) => {
            const count = event[key] ?? 0;

            for (let i = 0; i < count; i++) {
                const rowKey = `${key}-${i}`;
                let candidate;

                if (role === 'LEADER' && i === 0) {
                    candidate = event.leader;
                } else {
                    candidate = employeesByRole[role]?.[i];
                }

                const hasCandidate = !!candidate && candidate.id != null;

                const entry = {
                    isLocked: hasCandidate,
                    employee: hasCandidate ? candidate : null,
                };

                onLockChange?.(event.id, rowKey, entry);
            }

        });
    }, []); // 👈 ganz wichtig: nur einmal beim Mount!


    const handleSave = () => {
        handleUpdateEvent(localEditData);
    };

    return (
        <div className="w-full h-full">
            <div className="relative w-full h-full min-h-[500px] [perspective:1000px]">
                <div
                    className={`relative w-full h-full transition-transform duration-700 [transform-style:preserve-3d] ${
                        isFlipped ? '[transform:rotateY(180deg)]' : ''
                    }`}
                >
                    {/* FRONT SIDE */}
                    <div
                        className={`absolute inset-0 w-full h-full [backface-visibility:hidden] shadow-md rounded-xl p-4 flex flex-col ${
                            !isEditable ? 'bg-gray-200 border border-gray-300' : 'bg-white'
                        }  overflow-x-hidden relative ${isFlipped ? 'pointer-events-none' : 'pointer-events-auto'}`}
                        aria-hidden={isFlipped}
                    >
                        {/* Edit-Button */}
                        {isEditable && (
                            <button
                                onClick={() => setIsFlipped(true)}
                                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                            >

                                <img src={EditIcon} alt="Bearbeiten" className="h-5 w-5"/>
                            </button>
                        )}

                        <div className="flex flex-col space-y-4 h-full overflow-hidden">
                            <h2
                                className="text-xl font-semibold text-gray-800 line-clamp-2 flex-shrink-0"
                                title={event.name || 'Unbenanntes Event'}
                            >
                                {event.name || 'Unbenanntes Event'}
                            </h2>


                            <div className="flex items-center text-sm text-gray-600 gap-2 flex-shrink-0">
                                <img src={PinIcon} alt="Ort" className="h-4 w-4"/>
                                <span>{formatLocation(parseAddressString(event.location))}</span>
                            </div>

                            <div className="flex items-center text-sm text-gray-600 gap-2 flex-shrink-0">
                                <img src={ClockIcon} alt="Zeit" className="h-4 w-4"/>
                                <span>
                                    {formatDate(event.timeframe.startTime)} – {formatDate(event.timeframe.endTime)}
                                </span>
                            </div>

                            {/* Requirements + Languages combined */}
                            <div className="flex flex-wrap gap-2 mt-2 items-center flex-shrink-0">
                                {(() => {
                                    const colorMap = {
                                        KL: "bg-indigo-500 text-white",
                                        M: "bg-emerald-200 text-emerald-900",
                                        GM: "bg-rose-200 text-rose-900",
                                        DEFAULT: "bg-indigo-100 text-indigo-800",
                                    };
                                    const suffixOrder = { KL: 0, DEFAULT: 1, M: 2, GM: 3 };

                                    const items = (event.qualifications ?? []).map((q) => {
                                        const qName = typeof q === "string" ? q : q?.name ?? "";
                                        const m = qName.match(/^(.*?)(?:_(KL|M|GM))?$/);
                                        return { base: m?.[1] || qName, suffix: m?.[2] || "DEFAULT", raw: qName };
                                    });

                                    items.sort((a, b) => {
                                        const g = suffixOrder[a.suffix] - suffixOrder[b.suffix];
                                        return g !== 0 ? g : a.base.localeCompare(b.base, "de");
                                    });

                                    return items.map(({ base, suffix, raw }, idx) => (
                                        <span
                                            key={`q-front-${idx}-${raw}`}
                                            className={`text-xs px-3 py-1 rounded-full ${colorMap[suffix] || colorMap.DEFAULT}`}
                                            title={raw}
                                        >
                                          {base}
                                        </span>
                                    ));
                                })()}

                                {(event.languages ?? []).map((lang, idx) => {
                                    const code = typeof lang === 'string' ? lang : lang?.code ?? '';
                                    return (
                                        <span key={`lang-${idx}`} title={code} className="text-xl">
                                            {languageFlags[code] || code}
                                        </span>
                                    );
                                })}
                            </div>


                            <div className="mt-4 flex-1 min-h-0">
                                <div className="space-y-2 h-full overflow-y-auto pr-1">
                                    {[
                                        {key: 'numberLeader', icon: User, color: 'bg-indigo-500', role: 'LEADER'},
                                        {
                                            key: 'numberInstructors',
                                            icon: Users,
                                            color: 'bg-indigo-300',
                                            role: 'INSTRUCTOR'
                                        },
                                        {key: 'numberMechanics', icon: Tool, color: 'bg-emerald-300', role: 'MECHANIC'},
                                        {
                                            key: 'numberGuestManagers',
                                            icon: Coffee,
                                            color: 'bg-rose-300',
                                            role: 'GUEST_MANAGER'
                                        },
                                    ].flatMap(({key, icon, color, role}) => {
                                        const count = event[key] ?? 0;

                                        return Array.from({length: count}).map((_, idx) => {
                                            const rowKey = `${key}-${idx}`


                                            // Use the pill index (idx) to pick the matching candidate
                                            const lockEntry = lockedMap?.[rowKey];
                                            const candidate = lockEntry?.employee || (role === 'LEADER' && idx === 0
                                                ? event.leader
                                                : employeesByRole[role]?.[idx]);
                                            const isLocked = lockEntry?.isLocked ?? false;

                                            const hasCandidate = !!candidate && candidate.id != null;
                                            const borderClass = candidate?.id == null
                                                ? 'border-red-400'
                                                : doesEmployeeFulfillQualifications(candidate, role, event.qualifications)
                                                    ? 'border-gray-400'
                                                    : 'border-yellow-400';


                                            return (
                                                <div key={rowKey}
                                                     className="flex items-center gap-3 justify-between w-full">
                                                    <img src={icon} alt={key} className="h-5 w-5 flex-shrink-0"/>

                                                    <div className="flex-1 flex justify-center">
                                                        <div className="relative w-full max-w-[200px] min-w-[180px]">
                                                            <select
                                                                disabled={!isEditable}
                                                                value={candidate?.id || ''}
                                                                onFocus={() => handleDropdownFocus(rowKey, role)}
                                                                onChange={async (e) => {
                                                                    const selectedId = e.target.value;

                                                                    if (selectedId === "") {
                                                                        updateEmployeeInEvent(role, idx, null); // Clear the employee
                                                                        onLockChange?.(event.id, rowKey, {
                                                                            isLocked: false,
                                                                            employee: null,
                                                                        });
                                                                        await handleSave();
                                                                        return;
                                                                    }

                                                                    const selectedEmployee = (bestEmployeesMap[rowKey] || []).find(emp => emp.id === selectedId);
                                                                    if (!selectedEmployee) return;

                                                                    updateEmployeeInEvent(role, idx, selectedEmployee);

                                                                    onLockChange?.(event.id, rowKey, {
                                                                        isLocked: true,
                                                                        employee: selectedEmployee,
                                                                    });

                                                                    await handleSave();
                                                                }}
                                                                className={`w-full text-xs pl-5 pr-4 py-1 rounded-full bg-white text-gray-700 border-2 ${borderClass} focus:outline-none focus:ring-0 appearance-auto`}
                                                            >
                                                                {loadingMap[rowKey] ? (
                                                                    <option disabled>⏳ Lade Mitarbeiter...</option>
                                                                ) : (
                                                                    <>
                                                                        <optgroup label="Aktueller Mitarbeiter">
                                                                            <option value="">
                                                                                — Kein Mitarbeiter —
                                                                            </option>
                                                                            {candidate && (
                                                                                <option value={candidate.id}>
                                                                                    {candidate.firstName} {candidate.lastName}
                                                                                </option>
                                                                            )}
                                                                        </optgroup>


                                                                        {/* Available employees */}
                                                                        {(bestEmployeesMap[rowKey]?.filter(emp => emp.id !== candidate?.id) ?? []).length > 0 && (
                                                                            <optgroup label="Verfügbare Mitarbeiter">
                                                                                {bestEmployeesMap[rowKey]
                                                                                    .filter(emp => emp.id !== candidate?.id)
                                                                                    .map((emp) => (
                                                                                        <option key={emp.id}
                                                                                                value={emp.id}>
                                                                                            {emp.firstName} {emp.lastName}
                                                                                        </option>
                                                                                    ))}
                                                                            </optgroup>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </select>
                                                            <div
                                                                className={`absolute top-0 left-0 h-[98%] w-[6%] rounded-l-full pointer-events-none ${color}`}
                                                            ></div>
                                                        </div>
                                                    </div>

                                                    <div
                                                        className="relative"
                                                        onMouseEnter={() => setHoveredLockKey(rowKey)}
                                                        onMouseLeave={() => setHoveredLockKey(null)}
                                                    >
                                                        <button
                                                            onClick={() => {
                                                                if (!isEditable || !hasCandidate) return;
                                                                const currentEntry = lockedMap?.[rowKey] ?? {
                                                                    isLocked: false,
                                                                    employee: candidate
                                                                };

                                                                onLockChange?.(event.id, rowKey, {
                                                                    isLocked: !currentEntry.isLocked,
                                                                    employee: candidate,
                                                                });
                                                            }}
                                                            disabled={!isEditable || !hasCandidate}
                                                            className={`text-gray-500 transition ${
                                                                !hasCandidate || !isEditable
                                                                    ? 'opacity-40 cursor-not-allowed'
                                                                    : 'hover:text-indigo-500'
                                                            }`}
                                                        >
                                                            <img
                                                                src={
                                                                    !isEditable
                                                                        ? Lock // immer geschlossen wenn nicht editierbar
                                                                        : hasCandidate
                                                                            ? isLocked
                                                                                ? Lock
                                                                                : Unlock
                                                                            : Unlock
                                                                }
                                                                alt={isLocked ? 'Gesperrt' : 'Entsperrt'}
                                                                className="h-5 w-5"
                                                            />
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                                ;
                                        });
                                    })}
                                </div>
                            </div>
                        </div>
                        {showFadeTop && (
                            <>
                                <div
                                    className="pointer-events-none absolute inset-x-0 top-0 h-32 z-40
                                        bg-gradient-to-b from-black/35 via-black/15 to-transparent"
                                    aria-hidden="true"
                                />
                                <div
                                    className="absolute top-2 left-1/2 -translate-x-1/2 transform z-50
                                                 flex items-center gap-1 rounded-full bg-black/65 text-white
                                                 px-2 py-0.5 text-[10px] leading-none"
                                    title="Event läuft bereits"
                                >
                                    <span className="align-middle">Event läuft bereits</span>
                                </div>
                            </>
                        )}
                        {showFadeBottom && (
                            <>
                                <div
                                    className="pointer-events-none absolute inset-x-0 bottom-0 h-32 z-40
                                            bg-gradient-to-t from-black/35 via-black/15 to-transparent"
                                    aria-hidden="true"
                                />
                                <div
                                    className="absolute bottom-2 left-1/2 -translate-x-1/2 transform z-50
                                             flex items-center gap-1 rounded-full bg-black/65 text-white
                                             px-2 py-0.5 text-[10px] leading-none"
                                    title="Event läuft weiter"
                                >
                                    <span className="align-middle">Event läuft weiter</span>
                                </div>
                            </>
                        )}

                    </div>

                    {/* BACK SIDE – only name editable */}
                    {isEditable && (
                        <div
                           className={`absolute inset-0 w-full h-full [backface-visibility:hidden] [transform:rotateY(180deg)] bg-white shadow-md rounded-xl p-4 ${
                             isFlipped ? 'pointer-events-auto' : 'pointer-events-none'
                           }`}
                           aria-hidden={!isFlipped}                        >
                            <button
                                onClick={() => {
                                    setLocalEditData({
                                        name: event.name || '',
                                        timeframe: event.timeframe || {},
                                        location: parseAddressString(event.location),
                                        numberLeader: event.numberLeader || 0,
                                        numberInstructors: event.numberInstructors || 0,
                                        numberMechanics: event.numberMechanics || 0,
                                        numberGuestManagers: event.numberGuestManagers || 0,
                                        qualifications: Array.isArray(event.qualifications)
                                            ? event.qualifications.map((q) => (typeof q === 'string' ? q : q?.name || ''))
                                            : [],
                                        languages: Array.isArray(event.languages)
                                            ? event.languages.map((l) => (typeof l === 'string' ? l : l?.code || ''))
                                            : [],
                                    });
                                    setIsFlipped(false);
                                }}
                                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                            >
                                <img
                                    src={Plus}
                                    alt="speichern"
                                    className="h-5 w-5 pointer-events-none rotate-45"
                                />
                            </button>

                            <div className="mt-2">
                                {isEditingName ? (
                                    <input
                                        type="text"
                                        value={localEditData.name}
                                        onChange={(e) =>
                                            setLocalEditData((prev) => ({
                                                ...prev,
                                                name: e.target.value
                                            }))
                                        }
                                        onBlur={() => setIsEditingName(false)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') setIsEditingName(false);
                                        }}
                                        autoFocus
                                        className="w-full text-2xl font-semibold text-gray-800 border-b border-gray-300 focus:outline-none focus:border-indigo-500 bg-transparent"
                                    />
                                ) : (
                                    <h2
                                        onClick={() => setIsEditingName(true)}
                                        className="text-2xl font-semibold text-gray-800 cursor-pointer hover:text-indigo-600 transition truncate"
                                        title="Klicken zum Bearbeiten"
                                    >
                                        {localEditData.name || 'Unbenanntes Event'}
                                    </h2>
                                )}
                            </div>

                            {/* Address editing */}
                            <div className="mt-4">
                                <button
                                    onClick={() => setShowAddressModal(true)}
                                    className="flex items-center text-sm text-indigo-600 underline hover:text-indigo-800 gap-2 max-w-full"
                                >
                                    <img src={PinIcon} alt="Ort" className="h-4 w-4"/>
                                    <span className="truncate block text-left">
                                    {[
                                        localEditData.location?.street,
                                        localEditData.location?.streetNumber,
                                        localEditData.location?.postalCode,
                                        localEditData.location?.city,
                                        localEditData.location?.country,
                                    ]
                                        .filter(Boolean)
                                        .join(', ') || 'Adresse bearbeiten'}
                                </span>
                                </button>
                            </div>


                            <div className="mt-6">
                                <div className="grid grid-cols-2 gap-2">
                                    {/* Start time */}
                                    <div>
                                        <label
                                            className="block text-xs font-medium text-gray-700 mb-1">Startzeit</label>
                                        <input
                                            type="datetime-local"
                                            value={localEditData.timeframe.startTime?.slice(0, 16)}
                                            onChange={(e) =>
                                                setLocalEditData(prev => ({
                                                    ...prev,
                                                    timeframe: {
                                                        ...prev.timeframe,
                                                        startTime: e.target.value,
                                                    }
                                                }))
                                            }
                                            className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs"
                                        />
                                    </div>

                                    {/* End time */}
                                    <div>
                                        <label
                                            className="block text-xs font-medium text-gray-700 mb-1">Endzeit</label>
                                        <input
                                            type="datetime-local"
                                            value={localEditData.timeframe.endTime?.slice(0, 16)}
                                            onChange={(e) => {
                                                const newEndTime = e.target.value;
                                                const start = new Date(localEditData.timeframe.startTime);
                                                const end = new Date(newEndTime);

                                                if (end <= start) {
                                                    setTimeError(true);
                                                } else {
                                                    setTimeError(false);
                                                    setLocalEditData(prev => ({
                                                        ...prev,
                                                        timeframe: {
                                                            ...prev.timeframe,
                                                            endTime: newEndTime,
                                                        }
                                                    }));
                                                }
                                            }}
                                            className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs"
                                        />
                                        {timeError && (
                                            <p className="text-xs text-red-500 mt-1">
                                                Endzeit muss nach der Startzeit liegen.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>


                            {/* Qualifications editing */}
                            <div className="mt-4">
                                <div
                                    onClick={() => setShowQualificationModal(true)}
                                    className="relative flex items-center gap-2 overflow-x-auto max-w-full pr-6 cursor-pointer group flex-nowrap"
                                >
                                    {(() => {
                                        const colorMap = {
                                            KL: "bg-indigo-500 text-white",
                                            DEFAULT: "bg-indigo-100 text-indigo-800",
                                            M: "bg-emerald-200 text-emerald-900",
                                            GM: "bg-rose-200 text-rose-900",
                                        };
                                        const suffixOrder = { KL: 0, DEFAULT: 1, M: 2, GM: 3 };

                                        const list = (localEditData.qualifications || []).map((q) => {
                                            const m = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
                                            return { base: m?.[1] || q, suffix: m?.[2] || "DEFAULT", raw: q };
                                        });

                                        if (list.length === 0) {
                                            return (
                                                <span
                                                    className={`text-xs px-3 py-1 rounded-full ${colorMap.DEFAULT}`}
                                                    title="Klicken zum Bearbeiten"
                                                >
                                                    Qualifikation hinzufügen
                                                </span>
                                            );
                                        }

                                        list.sort((a, b) => {
                                            const g = suffixOrder[a.suffix] - suffixOrder[b.suffix];
                                            return g !== 0 ? g : a.base.localeCompare(b.base, "de");
                                        });

                                        const visibleCount = Math.min(list.length, 2);
                                        const visiblePills = list.slice(0, visibleCount);
                                        const displayPills = list.length > 3
                                            ? [...visiblePills, { base: "...", suffix: "DEFAULT", raw: "..." }]
                                            : list;

                                        return displayPills.map(({ base, suffix, raw }, idx) => (
                                            <span
                                                key={`q-back-${idx}-${raw}`}
                                                className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${
                                                    colorMap[suffix] || colorMap.DEFAULT
                                                }`}
                                                style={{ opacity: 1 - idx * 0.2 }}
                                                title="Klicken zum Bearbeiten"
                                            >
                                              {base}
                                            </span>
                                        ));
                                    })()}





                                    {localEditData.qualifications.length > 3 && (
                                        <div
                                            className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-r from-transparent to-white pointer-events-none rounded-r"
                                        ></div>
                                    )}
                                </div>
                            </div>


                            {/* Languages (Back Side) */}
                            <div className="mt-4">
                                <div className="flex flex-wrap items-center gap-2">
                                    {localEditData.languages.map((lang) => (
                                        <button
                                            key={lang}
                                            onClick={() => handleRemoveLanguage(lang)}
                                            className="flex items-center space-x-1 bg-indigo-100 text-indigo-800 text-sm px-2 py-1 rounded-full hover:bg-red-100 hover:text-red-600 transition"
                                            title="Klicken zum Entfernen"
                                        >
                                            <span>{languageFlags[lang] || lang}</span>
                                            <span className="text-xs font-bold">&times;</span>
                                        </button>
                                    ))}
                                    <div className="relative" ref={dropdownRef}>
                                        <button
                                            type="button"
                                            onClick={() => setShowLanguageSelect((prev) => !prev)}
                                            className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-600 hover:bg-indigo-500 hover:text-white transition leading-none text-base font-medium"
                                            title="Sprache hinzufügen"
                                        >
                                            <img
                                                src={Plus}
                                                alt="speichern"
                                                className="h-3 w-3 pointer-events-none"
                                            />
                                        </button>

                                        {showLanguageSelect && (
                                            <div
                                                className="absolute left-0 mt-2 z-20 bg-white border border-gray-200 rounded-md shadow-lg p-2 max-h-48 overflow-y-auto w-48">
                                                {Object.keys(languageFlags)
                                                    .filter((lang) => !localEditData.languages.includes(lang))
                                                    .map((lang) => (
                                                        <button
                                                            key={lang}
                                                            onClick={() => {
                                                                handleAddLanguage(lang);
                                                                setShowLanguageSelect(false);
                                                            }}
                                                            className="w-full text-left text-sm px-2 py-1 rounded hover:bg-indigo-100 hover:text-indigo-800 transition"
                                                        >
                                                            {languageFlags[lang]} {lang}
                                                        </button>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Number of persons editing */}
                            <div
                                className="relative group w-full h-32 grid grid-cols-4 text-center text-base font-medium mt-24"
                            >
                                {[
                                    {
                                        value: localEditData.numberLeader,
                                        key: 'numberLeader',
                                        bg: UserBG
                                    },
                                    {
                                        value: localEditData.numberInstructors,
                                        key: 'numberInstructors',
                                        bg: UsersBG
                                    },
                                    {
                                        value: localEditData.numberMechanics,
                                        key: 'numberMechanics',
                                        bg: ToolBG
                                    },
                                    {
                                        value: localEditData.numberGuestManagers,
                                        key: 'numberGuestManagers',
                                        bg: CoffeeBG
                                    },
                                ].map((item, idx) => (
                                    <div
                                        key={idx}
                                        className="relative flex items-center justify-center bg-white"
                                        style={{
                                            backgroundImage: `url(${item.bg})`,
                                            backgroundSize: '65%', // <-- Make the icons larger
                                            backgroundRepeat: 'no-repeat',
                                            backgroundPosition: 'center',
                                        }}
                                    >
                                        <input
                                            type="number"
                                            min={0}
                                            value={item.value}
                                            onChange={(e) => {
                                                const inputVal = parseInt(e.target.value) || 0;
                                                setLocalEditData(prev => ({
                                                    ...prev,
                                                    [item.key]: Math.max(0, inputVal),
                                                }));
                                            }}
                                            className="w-full h-full text-center text-4xl font-light text-black bg-transparent appearance-none focus:outline-none hover:appearance-auto transition-all"
                                            style={{MozAppearance: 'textfield'}}
                                        />
                                    </div>
                                ))}
                                {/* Shorter vertical dividers */}
                                <div
                                    className="absolute top-6 left-1/4 h-20 w-px bg-gray-300 pointer-events-none"/>
                                <div
                                    className="absolute top-6 left-2/4 h-20 w-px bg-gray-300 pointer-events-none"/>
                                <div
                                    className="absolute top-6 left-3/4 h-20 w-px bg-gray-300 pointer-events-none"/>
                            </div>


                            <div className="flex justify-center gap-3 mt-2">
                                <button
                                    onClick={handleDelete}
                                    type="button"
                                    className="rounded-full bg-red-500 px-4 py-2 text-xs text-white shadow-md hover:bg-red-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
                                >
                                    <img
                                        src={Trash}
                                        alt="löschen"
                                        className="h-5 w-5 pointer-events-none"
                                    />
                                </button>

                                <button
                                    type="button"
                                    onClick={handleSave}
                                    className="rounded-full bg-indigo-400 px-4 py-2 text-xs text-white shadow-md hover:bg-indigo-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400"
                                >
                                    <img
                                        src={Check}
                                        alt="speichern"
                                        className="h-5 w-5 pointer-events-none"
                                    />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {
                showAddressModal && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/10">
                        <div
                            className="relative bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl border border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">Adresse
                                bearbeiten</h2>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label
                                        className="block text-xs text-gray-500 mb-1">Straße</label>
                                    <input
                                        value={localEditData.location.street}
                                        onChange={(e) =>
                                            setLocalEditData((prev) => ({
                                                ...prev,
                                                location: {
                                                    ...prev.location,
                                                    street: e.target.value
                                                },
                                            }))
                                        }
                                        className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                    />
                                </div>
                                <div>
                                    <label
                                        className="block text-xs text-gray-500 mb-1">Hausnummer</label>
                                    <input
                                        value={localEditData.location.streetNumber}
                                        onChange={(e) =>
                                            setLocalEditData((prev) => ({
                                                ...prev,
                                                location: {
                                                    ...prev.location,
                                                    streetNumber: e.target.value
                                                },
                                            }))
                                        }
                                        className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                    />
                                </div>
                                <div>
                                    <label
                                        className="block text-xs text-gray-500 mb-1">Postleitzahl</label>
                                    <input
                                        value={localEditData.location.postalCode}
                                        onChange={(e) =>
                                            setLocalEditData((prev) => ({
                                                ...prev,
                                                location: {
                                                    ...prev.location,
                                                    postalCode: e.target.value
                                                },
                                            }))
                                        }
                                        className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                    />
                                </div>
                                <div>
                                    <label
                                        className="block text-xs text-gray-500 mb-1">Stadt</label>
                                    <input
                                        value={localEditData.location.city}
                                        onChange={(e) =>
                                            setLocalEditData((prev) => ({
                                                ...prev,
                                                location: {
                                                    ...prev.location,
                                                    city: e.target.value
                                                },
                                            }))
                                        }
                                        className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                    />
                                </div>
                                <div>
                                    <label
                                        className="block text-xs text-gray-500 mb-1">Land</label>
                                    <input
                                        value={localEditData.location.country}
                                        onChange={(e) =>
                                            setLocalEditData((prev) => ({
                                                ...prev,
                                                location: {
                                                    ...prev.location,
                                                    country: e.target.value
                                                },
                                            }))
                                        }
                                        className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                    />
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowAddressModal(false)}
                                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                                >
                                    Speichern
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                showQualificationModal && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/10 transition-opacity duration-300 animate-fadeIn">
                        <div
                            className="relative bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl border border-gray-200 transform transition-all duration-300 animate-scaleIn">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">Qualifikationen
                                bearbeiten</h2>
                            <div
                                className="flex flex-wrap gap-2 mb-4 max-h-[150px] overflow-y-auto pr-1">
                                {(() => {
                                    const colorMap = {
                                        KL: "bg-indigo-500 text-white",
                                        DEFAULT: "bg-indigo-100 text-indigo-800",
                                        M: "bg-emerald-200 text-emerald-900",
                                        GM: "bg-rose-200 text-rose-900",
                                    };
                                    const suffixOrder = { KL: 0, DEFAULT: 1, M: 2, GM: 3 };

                                    const list = (localEditData.qualifications || []).map((q) => {
                                        const m = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
                                        return { base: m?.[1] || q, suffix: m?.[2] || "DEFAULT", raw: q };
                                    });

                                    list.sort((a, b) => {
                                        const g = suffixOrder[a.suffix] - suffixOrder[b.suffix];
                                        return g !== 0 ? g : a.base.localeCompare(b.base, "de");
                                    });

                                    return list.map(({ base, suffix, raw }, idx) => (
                                        <button
                                            key={`q-modal-${idx}-${raw}`}
                                            onClick={() =>
                                                setLocalEditData((prev) => ({
                                                    ...prev,
                                                    qualifications: prev.qualifications.filter((x) => x !== raw),
                                                }))
                                            }
                                            className={`text-xs px-3 py-1 rounded-full ${colorMap[suffix] || colorMap.DEFAULT} hover:bg-red-100 hover:text-red-600 transition`}
                                            title="Klicken zum Entfernen"
                                        >
                                            {base}
                                        </button>
                                    ));
                                })()}

                            </div>
                            <div className="mb-4">
                                <select
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                    onChange={(e) => {
                                        const selected = e.target.value;
                                        if (selected && !localEditData.qualifications.includes(selected)) {
                                            setLocalEditData((prev) => ({
                                                ...prev,
                                                qualifications: [...prev.qualifications, selected],
                                            }));
                                        }
                                        e.target.value = "";
                                    }}
                                >
                                    <option value="">+ Qualifikation hinzufügen</option>
                                    {instructorOptions.length > 0 && (
                                        <optgroup label="Instruktoren">
                                            {instructorOptions.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                    {mechanicOptions.length > 0 && (
                                        <optgroup label="Mechaniker">
                                            {mechanicOptions.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                    {guestManagerOptions.length > 0 && (
                                        <optgroup label="Gästebetreuer">
                                            {guestManagerOptions.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                </select>
                            </div>
                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowQualificationModal(false)}
                                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                                >
                                    Speichern
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    )
        ;
};

export default EventCard;
